import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Search, Loader, ShieldAlert, CheckCircle2, XCircle, LogOut,
  CreditCard, Sticker, Calendar, TrendingUp, Shield, Wrench, Headphones,
  Smartphone, Gift, Ban, RefreshCw, Clock,
} from "lucide-react";

const PLAN_LABEL = { monthly: "Mensal", annual: "Anual" };
const MODE_LABEL = { recurring: "🔁 Renovação automática", one_time: "💳 Pagamento único" };
const STATUS_STYLE = {
  active: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Ativa" },
  pending: { bg: "bg-amber-100", text: "text-amber-700", label: "Aguardando pagamento" },
  expired: { bg: "bg-red-100", text: "text-red-700", label: "Expirada" },
  cancelled: { bg: "bg-slate-200", text: "text-slate-600", label: "Cancelada" },
};
const PAYMENT_STATUS_LABEL = {
  approved: { label: "Aprovado", color: "text-emerald-700 bg-emerald-50" },
  processed: { label: "Processado", color: "text-emerald-700 bg-emerald-50" },
  scheduled: { label: "Agendado", color: "text-indigo-700 bg-indigo-50" },
  pending: { label: "Pendente", color: "text-amber-700 bg-amber-50" },
  rejected: { label: "Rejeitado", color: "text-red-700 bg-red-50" },
  cancelled: { label: "Cancelado", color: "text-slate-600 bg-slate-100" },
};

const BENEFITS = [
  { icon: Sticker, title: "Película Hidrogel HD grátis", desc: "1 troca gratuita a cada 3 meses" },
  { icon: Shield, title: "3 meses extras de garantia", desc: "Em cima da garantia normal de qualquer reparo" },
  { icon: Wrench, title: "10% off em serviços", desc: "Manutenção, troca de tela, bateria, conector..." },
  { icon: Headphones, title: "15% off em acessórios", desc: "Fones, cabos, carregadores, capinhas" },
  { icon: Smartphone, title: "5% off em celulares novos", desc: "Toda a linha de aparelhos" },
  { icon: Gift, title: "Atendimento prioritário", desc: "Fila preferencial na loja e no WhatsApp" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function UpClubManage() {
  const [step, setStep] = useState("search");
  const [form, setForm] = useState({ email: "", phone: "" });
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("details");
  const [payments, setPayments] = useState({});
  const [selectedId, setSelectedId] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/upclub/lookup", form);
      const list = data.subscriptions || [];
      setSubs(list);
      setSelectedId(list[0]?.id || null);
      setStep("account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nenhuma assinatura encontrada");
    } finally { setLoading(false); }
  };

  const loadPayments = async (sub) => {
    if (payments[sub.id]) return;
    if (!sub.has_mp_recurring) { setPayments((p) => ({ ...p, [sub.id]: [] })); return; }
    try {
      const { data } = await api.post("/public/upclub/payments", {
        member_id: sub.id, email: form.email, phone: form.phone,
      });
      setPayments((p) => ({ ...p, [sub.id]: data.payments || [] }));
    } catch (err) {
      console.error("payments lookup:", err);
      setPayments((p) => ({ ...p, [sub.id]: [] }));
    }
  };

  const cancel = async (sub) => {
    if (!window.confirm(
      `Cancelar assinatura ${PLAN_LABEL[sub.plan_type]}?\n\nVocê continua com benefícios até ${fmtDate(sub.expires_at)}.`
    )) return;
    setLoading(true);
    try {
      await api.post("/public/upclub/cancel", {
        member_id: sub.id, email: form.email, phone: form.phone,
      });
      toast.success("Assinatura cancelada");
      const { data } = await api.post("/public/upclub/lookup", form);
      setSubs(data.subscriptions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cancelar");
    } finally { setLoading(false); }
  };

  const logout = () => {
    setStep("search"); setSubs([]); setPayments({}); setForm({ email: "", phone: "" });
  };

  if (step === "search") {
    return <SearchGate form={form} setForm={setForm} onSubmit={search} loading={loading} />;
  }
  const sub = subs.find((s) => s.id === selectedId) || subs[0];
  if (!sub) return <SearchGate form={form} setForm={setForm} onSubmit={search} loading={loading} empty />;
  const st = STATUS_STYLE[sub.status] || STATUS_STYLE.active;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <Link to="/upclub" className="inline-flex items-center gap-2 text-indigo-300 hover:text-white text-xs uppercase tracking-wider font-semibold">
            <Sparkles size={14} /> UP Club
          </Link>
          <button onClick={logout} data-testid="upclub-manage-logout" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white">
            <LogOut size={12} /> Sair
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-4 pb-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <h1 className="font-display text-3xl md:text-4xl font-bold">Minha Conta</h1>
              <p className="text-indigo-200 text-sm mt-1">
                Olá, <strong>{sub.client_name}</strong> — {sub.email}
              </p>
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full self-start md:self-end ${st.bg} ${st.text}`}>{st.label}</span>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 flex overflow-x-auto">
          <TabBtn active={tab === "details"} onClick={() => setTab("details")} icon={CreditCard} label="Detalhes" testid="tab-details" />
          <TabBtn active={tab === "payments"} onClick={() => { setTab("payments"); loadPayments(sub); }} icon={TrendingUp} label="Pagamentos" testid="tab-payments" />
          <TabBtn active={tab === "films"} onClick={() => setTab("films")} icon={Sticker} label="Películas" testid="tab-films" />
          <TabBtn active={tab === "benefits"} onClick={() => setTab("benefits")} icon={Gift} label="Benefícios" testid="tab-benefits" />
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {subs.length > 1 && (
          <div className="mb-4 flex gap-2 flex-wrap">
            {subs.map((s) => (
              <button key={s.id} onClick={() => setSelectedId(s.id)}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  s.id === selectedId ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-300 hover:border-indigo-300"
                }`}>
                {PLAN_LABEL[s.plan_type]} — {STATUS_STYLE[s.status]?.label}
              </button>
            ))}
          </div>
        )}
        {tab === "details" && <DetailsTab sub={sub} onCancel={() => cancel(sub)} loading={loading} />}
        {tab === "payments" && <PaymentsTab sub={sub} payments={payments[sub.id]} />}
        {tab === "films" && <FilmsTab sub={sub} />}
        {tab === "benefits" && <BenefitsTab sub={sub} />}
      </main>

      <footer className="max-w-4xl mx-auto px-4 py-6 text-center text-xs text-slate-500">
        Dúvidas?{" "}
        <a href="https://wa.me/5549998122884" target="_blank" rel="noreferrer" className="text-emerald-600 font-semibold hover:underline">
          Fala com a gente no WhatsApp
        </a>
      </footer>
    </div>
  );
}

function SearchGate({ form, setForm, onSubmit, loading, empty }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Link to="/upclub" className="inline-flex items-center gap-2 text-indigo-300 hover:text-white text-xs uppercase tracking-[0.25em] font-semibold mb-6">
          ← Voltar
        </Link>
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl" data-testid="upclub-manage-page">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-600 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-[0.2em] mb-3">
              <Sparkles size={12} /> UP Club
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900">Minha Conta</h1>
            <p className="text-slate-500 text-sm mt-2">
              Entre com seus dados pra ver sua assinatura, pagamentos e benefícios.
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <FieldLabel label="Email cadastrado *">
              <input type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="upclub-manage-email"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="cliente@email.com" />
            </FieldLabel>
            <FieldLabel label="Últimos 4 dígitos do WhatsApp *" hint="Ex: 49 99991-5433 → digita 5433">
              <input type="text" required minLength={4} maxLength={20} inputMode="numeric" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="upclub-manage-phone"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono tracking-widest"
                placeholder="5433" />
            </FieldLabel>
            <button type="submit" disabled={loading} data-testid="upclub-manage-search-btn"
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white font-semibold py-3 rounded-sm text-sm shadow-lg shadow-indigo-500/30 mt-2">
              {loading ? <Loader className="animate-spin" size={14} /> : <Search size={14} />}
              {loading ? "Buscando..." : "Entrar"}
            </button>
            {empty && <p className="text-xs text-red-500 text-center">Nenhuma assinatura encontrada.</p>}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3 mt-3">
              <ShieldAlert size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-800 leading-relaxed">Seus dados são usados só pra verificar sua identidade.</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DetailsTab({ sub, onCancel, loading }) {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Plano atual</div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-1">
            {PLAN_LABEL[sub.plan_type]} — {brl(sub.plan_price)}
            <span className="text-sm text-slate-500 font-normal">
              {sub.plan_type === "annual" ? " /ano" : " /mês"}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{MODE_LABEL[sub.billing_mode] || sub.billing_mode}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
          <InfoBlock label="Ativado em" icon={CheckCircle2} value={fmtDate(sub.activated_at)} />
          <InfoBlock label="Válida até" icon={Calendar} value={fmtDate(sub.expires_at)} highlight />
          <InfoBlock label="Próxima cobrança"
            icon={sub.status === "cancelled" ? Ban : RefreshCw}
            value={sub.status === "cancelled" ? "Cancelada" : fmtDate(sub.next_charge_date)} />
        </div>
      </div>
      {sub.can_cancel && sub.status !== "cancelled" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display text-lg font-bold mb-2">Cancelar assinatura</h3>
          <p className="text-sm text-slate-500 mb-4">
            Você mantém os benefícios até <strong>{fmtDate(sub.expires_at)}</strong>. Depois disso, não será mais cobrado.
          </p>
          <button onClick={onCancel} disabled={loading} data-testid={`upclub-manage-cancel-${sub.id}`}
            className="flex items-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 font-semibold py-2 px-4 rounded-sm text-sm">
            {loading ? <Loader className="animate-spin" size={14} /> : <XCircle size={14} />}
            Cancelar Assinatura
          </button>
        </div>
      )}
      {sub.status === "cancelled" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-3">
          <CheckCircle2 className="text-emerald-600 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-semibold text-emerald-800 mb-1">Assinatura cancelada</h3>
            <p className="text-sm text-emerald-700">
              Cancelada em {fmtDate(sub.cancelled_at)}. Benefícios até {fmtDate(sub.expires_at)}.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ sub, payments }) {
  if (payments === undefined) {
    return <div className="text-center py-12 text-slate-400"><Loader className="animate-spin mx-auto" size={20} /></div>;
  }
  if (!sub.has_mp_recurring) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <Clock className="mx-auto text-slate-400 mb-3" size={32} />
        <p className="text-sm text-slate-500">Essa assinatura não tem cobrança recorrente, então não há histórico de pagamentos.</p>
      </div>
    );
  }
  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
        <TrendingUp className="mx-auto text-slate-400 mb-3" size={32} />
        <p className="text-sm text-slate-500">Nenhum pagamento processado ainda.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-display text-lg font-bold">Histórico de Pagamentos</h3>
        <p className="text-xs text-slate-500 mt-0.5">{payments.length} cobrança{payments.length === 1 ? "" : "s"} — dados oficiais do Mercado Pago</p>
      </div>
      <div className="divide-y divide-slate-100">
        {payments.map((p) => {
          const st = PAYMENT_STATUS_LABEL[p.status] || { label: p.status, color: "text-slate-600 bg-slate-100" };
          return (
            <div key={p.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
              <div>
                <div className="text-sm font-semibold text-slate-800">{brl(p.amount)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{fmtDateTime(p.date)}</div>
                <div className="text-[10px] text-slate-400 font-mono mt-0.5">#{p.id?.slice(-10)}</div>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilmsTab({ sub }) {
  const history = sub.film_exchange_history || [];
  const last = sub.last_film_exchange;
  const has = history.length > 0 || last;
  const nextEligible = last
    ? new Date(new Date(last).getTime() + 90 * 86400000).toLocaleDateString("pt-BR")
    : "Hoje";
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Sticker className="text-indigo-600" size={20} />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold">Trocas de Película HD</h3>
            <p className="text-xs text-slate-500">1 grátis a cada 3 meses</p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Última troca</div>
            <div className="text-lg font-bold text-slate-800 mt-1">{last ? fmtDate(last) : "Nunca"}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Próxima grátis</div>
            <div className="text-lg font-bold text-indigo-600 mt-1">{nextEligible}</div>
          </div>
        </div>
      </div>
      {has && history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="p-4 border-b border-slate-100">
            <h4 className="font-semibold text-slate-800 text-sm">Histórico completo</h4>
          </div>
          <div className="divide-y divide-slate-100">
            {history.slice().reverse().map((h, idx) => (
              <div key={h.registered_at || `film-${idx}`} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                    {history.length - idx}
                  </div>
                  <div className="text-sm text-slate-700">{fmtDate(h.date)}</div>
                </div>
                <span className="text-[10px] text-slate-400">{fmtDateTime(h.registered_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!has && (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500">Você ainda não trocou nenhuma película. Passe na loja pra usar seu benefício.</p>
        </div>
      )}
    </div>
  );
}

function BenefitsTab({ sub }) {
  const isActive = sub.status === "active";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {BENEFITS.map((b) => {
        const Icon = b.icon;
        return (
          <div key={b.title} className={`flex gap-3 p-4 rounded-2xl border ${isActive ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-60"}`}>
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 border border-indigo-200 flex items-center justify-center">
              <Icon size={18} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm text-slate-800 flex items-center justify-between gap-2">
                {b.title}
                {isActive && <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{b.desc}</div>
            </div>
          </div>
        );
      })}
      {!isActive && (
        <div className="col-span-full text-center text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
          Sua assinatura não está ativa — os benefícios estão inacessíveis.
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
        active ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}>
      <Icon size={15} /> {label}
    </button>
  );
}

function InfoBlock({ label, icon: Icon, value, highlight }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className={`text-base font-bold mt-1 ${highlight ? "text-indigo-600" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function FieldLabel({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-600 font-semibold mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
